import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { companyLabel } from "@/actividades/CompanyPicker";
import type { Branch, Company, Contact } from "@/api/queries";
import { useBranches, useCompanyContacts, useCompanyOptions } from "@/api/queries";
import { NuevaEmpresa } from "@/empresas/NuevaEmpresa";
import { NuevaSucursal } from "@/empresas/NuevaSucursal";
import { NuevoContacto } from "@/empresas/NuevoContacto";
import { useSettled } from "@/lib/useSettled";
import type { Call } from "./state";
import { useLinkCall } from "./queries";
import type { Picked, SearchOption } from "./SearchSelect";
import { folded, SearchSelect } from "./SearchSelect";

type Step = "company" | "branch" | "contact";

/** Who the agent says the call was with, until saving the write-up files it. */
export type Assignment = { company: Picked | null; contact: Picked | null };

export const NO_ASSIGNMENT: Assignment = { company: null, contact: null };

/**
 * What a call to an existing account still needs filed, if anything: the
 * blanks on the call that the agent has chosen something for. `null` when
 * there is nothing to send.
 */
export function pendingLink(
  call: Pick<Call, "company" | "contact">,
  assignment: Assignment,
): { company?: number; contact?: number } | null {
  const company = call.company == null ? assignment.company?.id : undefined;
  const contact = call.contact == null ? assignment.contact?.id : undefined;
  if (company === undefined && contact === undefined) return null;
  return {
    ...(company !== undefined ? { company } : {}),
    ...(contact !== undefined ? { contact } : {}),
  };
}

/** Two letters narrow 25,000 companies to something a list can show; one does not. */
const MIN_LETTERS = 2;
/** What fits in the bar without pushing the write-up off the screen. */
const SHOWN = 8;

/**
 * Who a call dialled on *Llamar* was with: a number nobody has on file, so the
 * call starts with no company and no contact.
 *
 * Usually the account exists already, under another number: *Empresa* finds
 * it as the name is typed, and *Contacto* then lists the people at it. Where
 * nobody there is the person who answered, *Crear contacto* adds them — and
 * they appear in the list, chosen, the moment they exist. Where the company
 * itself is new, *Registrar empresa* creates it, files the call under it at
 * once (`useLinkCall`) and walks on to its branch and its first contact.
 *
 * What is *chosen* is filed when the write-up is saved, not when it is picked:
 * a call can be filed only once (`link_call` fills blanks and nothing else),
 * and a slip of the arrow keys should not be the account a call stays under.
 *
 * A seller is offered their own companies and the unassigned ones, never a
 * colleague's (`?scope=workable`) — the server would refuse it anyway.
 */
export function CallProspect({
  call,
  assignment,
  onAssign,
}: {
  call: Call;
  assignment: Assignment;
  onAssign: (assignment: Assignment) => void;
}) {
  const { t } = useTranslation();
  const link = useLinkCall();
  const [step, setStep] = useState<Step | null>(null);

  if (call.contact != null) {
    return (
      <span className="text-ground/70">
        {t("call.prospect.filedUnder", {
          name: `${call.company_name ?? ""} · ${call.contact_name ?? ""}`,
        })}
      </span>
    );
  }

  // The company the contact is chosen from: the one the call is filed under,
  // else the one picked.
  const company: Picked | null =
    call.company != null
      ? { id: call.company, label: call.company_name ?? "" }
      : assignment.company;

  return (
    <div className="space-y-2">
      <p className="text-ground/70">
        {call.company == null
          ? t("call.prospect.lead")
          : t("call.prospect.filedUnder", { name: call.company_name ?? "" })}
      </p>

      {call.company == null ? (
        <div className="flex flex-wrap items-end gap-2">
          <CompanySearch
            value={assignment.company}
            onPick={(picked) => onAssign({ company: picked, contact: null })}
          />
          <StepButton onClick={() => setStep("company")} disabled={link.isPending}>
            {t("call.prospect.createCompany")}
          </StepButton>
        </div>
      ) : null}

      {company ? (
        <ContactSearch
          // A list, its search and what was created belong to one company.
          key={company.id}
          company={company}
          dialedNumber={call.dialed_number}
          value={assignment.contact}
          onPick={(picked) => onAssign({ ...assignment, contact: picked })}
          step={step}
          onStep={setStep}
        />
      ) : null}

      {pendingLink(call, assignment) ? (
        <p className="text-xs text-ground/70">{t("call.prospect.linksOnSave")}</p>
      ) : null}
      {link.isError ? (
        <p role="alert" className="text-ground/90">
          {link.error.message}
        </p>
      ) : null}

      {/* In `document.body`, not here: this sits inside the wrap-up's
          `<form>`, where Enter in a field of the modal would submit the
          write-up, and inside `.on-dark`, whose focus ring is drawn for a dark
          ground and vanishes on the modal's light one. */}
      {call.company == null
        ? createPortal(
            <NuevaEmpresa
              open={step === "company"}
              onClose={() => setStep(null)}
              onCreated={(created) =>
                link.mutate(
                  { ref: call.ref, company: created.id },
                  {
                    onSuccess: () => {
                      // Filed under the new one: nothing picked is left to send.
                      onAssign(NO_ASSIGNMENT);
                      setStep("branch");
                    },
                  },
                )
              }
            />,
            document.body,
          )
        : null}
    </div>
  );
}

/** *Empresa*: the server's search, narrowed to what the agent may file under. */
function CompanySearch({
  value,
  onPick,
}: {
  value: Picked | null;
  onPick: (picked: Picked | null) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(value?.label ?? "");

  const term = text.trim();
  const searching = value === null && term.length >= MIN_LETTERS;
  const settled = useSettled(searching ? term : "");
  const companies = useCompanyOptions(settled, "workable");
  const options = settled ? ranked(companies.data?.results ?? [], settled) : [];
  const shown = options.slice(0, SHOWN);
  const stale = settled !== term || companies.isPending || companies.isPlaceholderData;
  const total = companies.data?.count ?? 0;

  function ownership(company: Company): string {
    if (company.assignment_rank === 1) return t("newFollowUp.companyMine");
    if (company.assignment_rank === 3) return t("newFollowUp.companyUnassigned");
    // Only a manager gets here: `workable` leaves a colleague's out for a seller.
    return t("newFollowUp.companyTheirs", { agent: company.current_agent_name ?? "—" });
  }

  const status = !searching
    ? t("call.prospect.typeToSearch", { count: MIN_LETTERS })
    : stale && shown.length === 0
      ? t("newFollowUp.companySearching")
      : companies.isError
        ? t("newFollowUp.companySearchFailed")
        : shown.length === 0
          ? t("newFollowUp.companyNoMatches", { term })
          : null;

  return (
    <SearchSelect
      label={t("call.prospect.company")}
      placeholder={t("call.prospect.companyPlaceholder")}
      value={value}
      text={text}
      onText={setText}
      onPick={onPick}
      options={shown.map((company) => {
        const label = companyLabel(company);
        return {
          id: company.id,
          label,
          detail: [
            company.name && company.legal_name.trim() !== label
              ? company.legal_name.trim()
              : "",
            company.city ?? "",
            ownership(company),
          ]
            .filter(Boolean)
            .join(" · "),
        };
      })}
      status={status}
      footer={
        total > shown.length && !stale
          ? t("newFollowUp.companyMore", {
              shown: shown.length,
              count: total.toLocaleString("es-MX"),
            })
          : null
      }
    />
  );
}

/**
 * *Contacto*: the people at the chosen company, filtered here as the name is
 * typed — they are a handful, already loaded in one request, and every key
 * should narrow them at once. *Crear contacto* beside it, for when the person
 * who answered is not among them.
 */
function ContactSearch({
  company,
  dialedNumber,
  value,
  onPick,
  step,
  onStep,
}: {
  company: Picked;
  dialedNumber: string;
  value: Picked | null;
  onPick: (picked: Picked | null) => void;
  step: Step | null;
  onStep: (step: Step | null) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(value?.label ?? "");
  const contacts = useCompanyContacts(company.id);
  // What was just created, until the list query catches up with it: it is
  // chosen on the same tick, and has to be in the list it is chosen from.
  const [created, setCreated] = useState<Contact[]>([]);
  const [createdBranches, setCreatedBranches] = useState<Branch[]>([]);

  const listedBranches = useBranches(company.id).data?.results ?? [];
  const branches = [
    ...listedBranches,
    ...createdBranches.filter(
      (branch) => !listedBranches.some((known) => known.id === branch.id),
    ),
  ];
  const hasBranch = branches.length > 0;

  const listed = contacts.data?.results ?? [];
  const people = [
    ...listed,
    ...created.filter((contact) => !listed.some((known) => known.id === contact.id)),
  ]
    // Somebody who has left cannot be who answered.
    .filter((contact) => !contact.disabled_at);
  const term = folded(text);
  const matches = value
    ? []
    : people
        .filter(
          (contact) =>
            folded(contact.name).includes(term) ||
            folded(contact.job_title ?? "").includes(term),
        )
        .sort(
          (a, b) =>
            Number(!folded(a.name).startsWith(term)) -
            Number(!folded(b.name).startsWith(term)),
        );
  const options: SearchOption[] = matches.map((contact) => ({
    id: contact.id,
    label: contact.name,
    ...(contact.job_title ? { detail: contact.job_title } : {}),
  }));

  const status = contacts.isPending
    ? t("call.prospect.contactsLoading")
    : contacts.isError
      ? t("call.prospect.contactsFailed")
      : people.length === 0
        ? t("call.prospect.noContacts")
        : options.length === 0
          ? t("call.prospect.contactNoMatches", { term: text.trim() })
          : null;

  return (
    <div className="flex flex-wrap items-end gap-2">
      <SearchSelect
        label={t("call.prospect.contact")}
        placeholder={t("call.prospect.contactPlaceholder")}
        value={value}
        text={text}
        onText={setText}
        onPick={onPick}
        options={options}
        status={status}
      />
      {/* A contact hangs off a branch: a company without one gets it first. */}
      <StepButton onClick={() => onStep(hasBranch ? "contact" : "branch")}>
        {t("call.prospect.createContact")}
      </StepButton>

      {createPortal(
        <>
          <NuevaSucursal
            open={step === "branch"}
            onClose={() => onStep(null)}
            companyId={company.id}
            companyName={company.label}
            onCreated={(branch) => {
              setCreatedBranches((previous) => [...previous, branch]);
              onStep("contact");
            }}
          />
          <NuevoContacto
            open={step === "contact" && hasBranch}
            onClose={() => onStep(null)}
            companyId={company.id}
            companyName={company.label}
            branches={branches}
            initialPhone={dialedNumber}
            onCreated={(contact) => {
              setCreated((previous) => [...previous, contact]);
              setText(contact.name);
              onPick({ id: contact.id, label: contact.name });
            }}
          />
        </>,
        document.body,
      )}
    </div>
  );
}

/**
 * Names that start with what was typed come first; otherwise the server's own
 * order stands — the agent's accounts, then the unassigned.
 */
function ranked(results: Company[], term: string): Company[] {
  const wanted = folded(term);
  const rank = (company: Company) =>
    folded(companyLabel(company)).startsWith(wanted) ? 0 : 1;
  return [...results].sort((a, b) => rank(a) - rank(b));
}

/** The call bar's own button: the system's assumes a light ground. */
function StepButton({
  onClick,
  disabled = false,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="border-2 border-ground/40 px-3 py-1.5 text-ground hover:bg-ground/15 disabled:opacity-50"
    >
      {children}
    </button>
  );
}
